import { datadogRum, RumErrorEvent, RumEvent, RumInitConfiguration } from '@datadog/browser-rum'
//import { reactPlugin } from '@datadog/browser-rum-react'

// Ported from the old Sentry `ignoreErrors`. `ChunkLoadError` is deliberately absent:
// `catchError` in error/setupErrorCapture.ts reports it through `addError`, and it is
// the only signal that a deploy shipped assets the running index.html cannot load.
const ignoredErrors: (string | RegExp)[] = [
  // Cross-origin scripts without `crossorigin="anonymous"` surface every error as this
  // opaque message; a single bot session produced thousands of them (DEV-3362).
  'Script error.',
  /Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+/,
  'ResizeObserver loop limit exceeded',
  // Bots
  /Object Not Found Matching Id.+MethodName/,
  // Tawk
  "Cannot read properties of undefined (reading '$socket')",
]

export const MAX_PER_SESSION = 20

// Only the fields the predicate reads. RumEvent is assignable to this, and tests can
// build events without filling in the dozens of required RUM envelope fields.
export type SendableEvent =
  | { type: 'error'; error: Pick<RumErrorEvent['error'], 'message'> }
  | { type: Exclude<RumEvent['type'], 'error'> }

const isIgnored = (message: string) =>
  ignoredErrors.some(m => (typeof m === 'string' ? message === m : m.test(message)))

// Fresh per-session counters, so a runaway loop stops reporting after MAX_PER_SESSION
// identical messages instead of filling the RUM quota.
export const createBeforeSend = () => {
  const seen = new Map<string, number>()

  return (event: SendableEvent): boolean => {
    if (event.type !== 'error') return true
    const { message } = event.error
    if (isIgnored(message)) return false
    const n = (seen.get(message) ?? 0) + 1
    seen.set(message, n)
    return n <= MAX_PER_SESSION
  }
}

export const beforeSend: NonNullable<RumInitConfiguration['beforeSend']> = createBeforeSend()

if (process.env.DATADOG_APPLICATION_ID) {
  const init: RumInitConfiguration = {
    applicationId: process.env.DATADOG_APPLICATION_ID,
    clientToken: process.env.DATADOG_CLIENT_TOKEN as string,
    site: process.env.DATADOG_SITE as RumInitConfiguration['site'],
    env: process.env.DATADOG_ENV as string,
    service: 'app',
    // TODO: version: process.env.DATADOG_VERSION as string,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend,
    // For some reason causes no errors being posted
    // plugins: [reactPlugin({ router: true })],
  }

  datadogRum.init(init)
}
